import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addAdminTaskComment,
  getAdminTaskDetail,
  type AdminTaskDetail,
  type AdminTaskDetailComment,
} from "../api/adminTasks";
import {
  deleteTask,
  finalizeQuote,
  getSnapshot,
  getStateHistory,
  getTask,
  getTaskApplications,
  listTasks,
  respondAndCloseTask,
  updateTask,
  type FinalizeQuoteRequest,
  type FinalizeQuoteResponse,
  type ListTasksQuery,
  type PaginatedTaskStateHistory,
  type PaginatedTasks,
  type RespondAndCloseTaskResponse,
  type TaskApplicationLink,
  type TaskDetail,
  type TaskSnapshot,
  type UpdateTaskBody,
} from "../api/tasks";

export const TASKS_KEY = "tasks";

/**
 * Eager-loaded admin Edit-Task modal payload (svc-04 `/v1/admin/tasks/:id`).
 * Backs `TaskEditModal`; the read-only `TaskDetailModal` still uses the
 * older fan-out hooks because it predates this endpoint.
 */
export function useAdminTaskDetail(
  taskId: string | null | undefined,
  enabled = true,
) {
  return useQuery<AdminTaskDetail>({
    queryKey: [TASKS_KEY, "admin-detail", taskId],
    queryFn: () => getAdminTaskDetail(taskId as string),
    enabled: !!taskId && enabled,
    retry: false,
  });
}

/** Append a comment to a task's thread; refreshes the admin Edit-Task detail. */
export function useAddTaskComment() {
  const qc = useQueryClient();
  return useMutation<
    AdminTaskDetailComment,
    Error,
    { taskId: string; comment: string }
  >({
    mutationFn: ({ taskId, comment }) => addAdminTaskComment(taskId, comment),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: [TASKS_KEY, "admin-detail", vars.taskId],
      });
    },
  });
}

export function useTasksQueue(query: ListTasksQuery = {}, enabled = true) {
  return useQuery<PaginatedTasks>({
    queryKey: [TASKS_KEY, "queue", query],
    queryFn: () => listTasks(query),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

export function useTask(taskId: string | null | undefined, enabled = true) {
  return useQuery<TaskDetail>({
    queryKey: [TASKS_KEY, "detail", taskId],
    queryFn: () => getTask(taskId as string),
    enabled: !!taskId && enabled,
    retry: false,
  });
}

export function useTaskSnapshot(
  taskId: string | null | undefined,
  enabled = true,
) {
  return useQuery<TaskSnapshot | null>({
    queryKey: [TASKS_KEY, "snapshot", taskId],
    queryFn: () => getSnapshot(taskId as string),
    enabled: !!taskId && enabled,
    retry: false,
  });
}

export function useTaskApplications(
  taskId: string | null | undefined,
  enabled = true,
) {
  return useQuery<TaskApplicationLink[]>({
    queryKey: [TASKS_KEY, "applications", taskId],
    queryFn: () => getTaskApplications(taskId as string),
    enabled: !!taskId && enabled,
    retry: false,
  });
}

export function useTaskStateHistory(
  taskId: string | null | undefined,
  query: { limit?: number; offset?: number } = {},
  enabled = true,
) {
  return useQuery<PaginatedTaskStateHistory>({
    queryKey: [TASKS_KEY, "state-history", taskId, query],
    queryFn: () => getStateHistory(taskId as string, query),
    enabled: !!taskId && enabled,
    retry: false,
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (taskId) => deleteTask(taskId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [TASKS_KEY, "queue"] });
    },
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation<
    TaskDetail,
    Error,
    { taskId: string; body: UpdateTaskBody }
  >({
    mutationFn: ({ taskId, body }) => updateTask(taskId, body),
    onSuccess: (data) => {
      void qc.invalidateQueries({
        queryKey: [TASKS_KEY, "detail", data.taskId],
      });
      void qc.invalidateQueries({
        queryKey: [TASKS_KEY, "admin-detail", data.taskId],
      });
      void qc.invalidateQueries({ queryKey: [TASKS_KEY, "queue"] });
      void qc.invalidateQueries({
        queryKey: [TASKS_KEY, "state-history", data.taskId],
      });
    },
  });
}

export function useRespondAndCloseTask() {
  const qc = useQueryClient();
  return useMutation<
    RespondAndCloseTaskResponse,
    Error,
    { taskId: string; response: string }
  >({
    mutationFn: ({ taskId, response }) => respondAndCloseTask(taskId, response),
    onSuccess: (data) => {
      void qc.invalidateQueries({
        queryKey: [TASKS_KEY, "detail", data.taskId],
      });
      void qc.invalidateQueries({
        queryKey: [TASKS_KEY, "admin-detail", data.taskId],
      });
      void qc.invalidateQueries({ queryKey: [TASKS_KEY, "queue"] });
    },
  });
}

export function useFinalizeQuote() {
  const qc = useQueryClient();
  return useMutation<
    FinalizeQuoteResponse,
    Error,
    { taskId: string; body?: FinalizeQuoteRequest }
  >({
    mutationFn: ({ taskId, body }) => finalizeQuote(taskId, body),
    onSuccess: (data) => {
      void qc.invalidateQueries({
        queryKey: [TASKS_KEY, "detail", data.taskId],
      });
      void qc.invalidateQueries({
        queryKey: [TASKS_KEY, "admin-detail", data.taskId],
      });
      void qc.invalidateQueries({ queryKey: [TASKS_KEY, "queue"] });
    },
  });
}
